import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

function SectionOne({ isProduct }) {
  const [mainImage, setMainImage] = useState(""); // الصورة الكبيرة
  const [galleryImages, setGalleryImages] = useState([]); // المعرض
  const [sliderOpen, setSliderOpen] = useState(false); // حالة فتح الشريط
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // الفهرس المحدد للصورة
  const [visibleThumbnailsStart, setVisibleThumbnailsStart] = useState(0); // لتحديد بداية عرض الصور المصغرة
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (isProduct) {
      const mainImageObject = { image_url: isProduct.image_url }; // الصورة الرئيسية
      const gallery = isProduct.gallery || []; // المعرض من المنتج
      const updatedGallery = [mainImageObject, ...gallery]; // دمج الصورة الرئيسية مع المعرض

      setMainImage(mainImageObject.image_url);
      setGalleryImages(updatedGallery);
    }
  }, [isProduct]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const sliderContent = document.getElementById("slider-content");
      if (sliderOpen && sliderContent && !sliderContent.contains(event.target)) {
        setSliderOpen(false);
      }
    };

    const handleKeyPress = (event) => {
      if (event.key === "Escape") {
        setSliderOpen(false);
      }
    };

    if (sliderOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [sliderOpen]);

  useEffect(() => {
    let interval;
    if (galleryImages.length > 1 && !sliderOpen && !isHovered) {
      interval = setInterval(() => {
        handleNextImage();
      }, 3000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [selectedImageIndex, galleryImages, sliderOpen, isHovered]);

  const handleThumbnailClick = (index) => {
    setSelectedImageIndex(index);
    setMainImage(galleryImages[index].image_url);
  };

  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      const newIndex = selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      setMainImage(galleryImages[newIndex].image_url);
    }
    if (visibleThumbnailsStart > 0) {
      setVisibleThumbnailsStart(visibleThumbnailsStart - 1);
    }
  };

  const handleNextImage = () => {
    if (selectedImageIndex < galleryImages.length - 1) {
      const newIndex = selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      setMainImage(galleryImages[newIndex].image_url);
    } else {
      // Loop back to the first image
      setSelectedImageIndex(0);
      setMainImage(galleryImages[0].image_url);
    }
    if (visibleThumbnailsStart + 4 < galleryImages.length) {
      setVisibleThumbnailsStart(visibleThumbnailsStart + 1);
    } else {
      // Reset visible thumbnails when looping back
      setVisibleThumbnailsStart(0);
    }
  };

  return (
    <>
      <Helmet>
        {/* <title>{isProduct?.title || ""} | {t("ASG")}</title> */}
        <meta
          name="description"
          content={isProduct?.description?.slice(0, 160)}
        />
        <meta name="keywords" content={`${isProduct?.category?.name}, ${isProduct?.brand?.name}, ${isProduct?.title}`} />
        <meta property="og:title" content={isProduct?.title} />
        <meta property="og:description" content={isProduct?.description?.slice(0, 160)} />
        <meta property="og:type" content="product" />
        <meta property="og:image" content={isProduct?.image_url} />
        <meta property="og:url" content={window.location.href} />
      </Helmet>
      <div className="flex flex-col lg:flex-row mt-[10rem] lg:items-start items-start gap-5 lg:gap-10 w-full pb-10">
        <div
          data-aos="fade-in"
          data-aos-duration="1000"
          data-aos-easing="linear"
          data-aos-delay="500"
          className="flex flex-col-reverse lg:flex-row items-center gap-4 lg:gap-7"
        >
          {galleryImages.length > 1 ? (
            <div className="relative flex flex-row lg:flex-col items-center justify-center gap-3 w-full lg:w-auto">
              <div className="flex flex-row lg:flex-col items-center gap-2 overflow-x-auto lg:overflow-y-auto w-full lg:w-auto">
                {galleryImages
                  .slice(visibleThumbnailsStart, visibleThumbnailsStart + 4)
                  .map((item, index) => {
                    const isVideo = item?.image_url?.match(/\.(mp4|mov|avi|webm)$/i);

                    return isVideo ? (
                      <div
                        key={index}
                        className={`relative w-[15vw] h-[15vw] sm:w-[15vw] sm:h-[15vw] md:w-[15vw] md:h-[15vw] lg:w-[5vw] lg:h-[5vw] cursor-pointer rounded-md overflow-hidden ${selectedImageIndex === visibleThumbnailsStart + index
                          ? "border-2 border-blue-500"
                          : ""
                          }`}
                        onClick={() => handleThumbnailClick(visibleThumbnailsStart + index)}
                      >
                        {/* الفيديو نفسه */}
                        <video
                          src={item.image_url}
                          className="w-full h-full object-cover pointer-events-none" // يمنع التشغيل
                          muted
                        />
                        {/* زر تشغيل فوق الفيديو */}
                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                          <span className="text-white text-3xl">▶</span>
                        </div>
                      </div>
                    ) : (
                      <img
                        key={index}
                        src={item.image_url}
                        alt="Thumbnail"
                        className={`w-[15vw] h-[15vw] sm:w-[15vw] sm:h-[15vw] md:w-[15vw] md:h-[15vw] lg:w-[5vw] lg:h-[5vw] cursor-pointer rounded-md object-cover ${selectedImageIndex === visibleThumbnailsStart + index
                          ? "border-2 border-blue-500"
                          : ""
                          }`}
                        onClick={() => handleThumbnailClick(visibleThumbnailsStart + index)}
                      />
                    );
                  })}
              </div>
            </div>
          ) : (
            <div></div>
          )}
          <div className="flex justify-center w-full">
            {mainImage?.match(/\.(mp4|mov|avi|webm)$/i) ? (
              <video
                controls
                className="w-[100vw] h-[70vw] md:w-[100vw] md:h-[70vw] lg:w-[65vw] lg:h-[30vw] xl:max-h-[25vw] xl:w-[40vw] rounded-md object-cover cursor-pointer"
                onClick={() => setSliderOpen(true)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <source src={mainImage} type="video/mp4" />
                متصفحك لا يدعم تشغيل الفيديو
              </video>
            ) : (
              <img
                src={mainImage}
                alt="Selected"
                className="w-[100vw] h-[70vw] md:w-[100vw] md:h-[70vw] lg:w-[65vw] lg:h-[30vw] xl:max-h-[25vw] xl:w-[40vw] rounded-md object-cover cursor-pointer"
                onClick={() => setSliderOpen(true)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              />
            )}
          </div>
        </div>
        <div data-aos="fade-left"
          data-aos-duration="1000"
          data-aos-easing="linear"
          data-aos-delay="500" className="flex flex-col text-start gap-[1rem] lg:gap-6 lg:w-[60vw]"
          style={{ fontSize: "clamp(1rem, 1vw, 4rem)" }}>
          <h1 className="text-[#0A66B0] font-[700]"
            style={{ fontSize: "clamp(1.6rem, 1.6vw, 4rem)" }}>{isProduct?.title}</h1>
          <p className="text-start text-[#141414] font-[400]">
            {isProduct?.description}</p>
          <span className="border"></span>
          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-1 gap-2">
            <p className="text-start text-[#141414] font-[400]">
              <span className="text-[#5F5F5F]">{t("catalog")} : </span>
              {isProduct?.category?.name}
            </p>
            <p className="text-start text-[#141414] font-[400]">
              <span className="text-[#5F5F5F]">{t("subCatalog")} : </span>
              {isProduct?.subcategory?.name}
            </p>
            <p className="text-start text-[#141414] font-[400]">
              <span className="text-[#5F5F5F]">{t("brand")} : </span>
              {isProduct?.brand?.name}
            </p>
            <p className="text-start text-[#141414] font-[400]">
              <span className="text-[#5F5F5F]">{t("subBrand")} : </span>
              {isProduct?.subbrand?.name}
            </p>
            <p className="text-start text-[#141414] font-[400]">
              <span className="text-[#5F5F5F]">{t("sku")} :</span> {isProduct?.sku}
            </p>
          </div>
          <Link
            to={`/order/${isProduct?.title}`}
            className="me-auto"
          >
            <button className="bg-[#0A66B0] hover:bg-transparent border-[#0A66B0 border-[1px] hover:border-[#0A66B0] text-[#FFF] hover:text-[#0A66B0] font-[400] py-2 px-8 rounded-md">
              {t("orderNow")}
            </button>
          </Link>
        </div>


        {sliderOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex justify-center items-center">
            <div
              id="slider-content"
              className="relative flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* زر الإغلاق */}
              <span
                className="absolute top-4 right-4 z-50 flex items-center justify-center 
               w-10 h-10 rounded-full bg-black bg-opacity-60 
               text-white text-2xl shadow-lg cursor-pointer 
               hover:bg-opacity-80 transition duration-200"
                onClick={() => setSliderOpen(false)}
              >
                &#10005;
              </span>

              {/* السهم لليسار */}
              {selectedImageIndex > 0 && (
                <span
                  className="absolute left-4 z-50 flex items-center justify-center 
                 w-12 h-12 rounded-full bg-black bg-opacity-60 
                 text-white text-3xl shadow-lg cursor-pointer 
                 hover:bg-opacity-80 transition duration-200"
                  onClick={handlePrevImage}
                >
                  &#10094;
                </span>
              )}

              {/* المحتوى: صورة أو فيديو */}
              {galleryImages[selectedImageIndex]?.image_url?.match(/\.(mp4|mov|avi|webm)$/i) ? (
                <video
                  controls
                  autoPlay
                  className="max-h-[90vh] max-w-[90vw] mx-auto rounded-md z-40"
                >
                  <source
                    src={galleryImages[selectedImageIndex]?.image_url}
                    type="video/mp4"
                  />
                  متصفحك لا يدعم تشغيل الفيديو
                </video>
              ) : (
                <img
                  id="slider-image"
                  src={galleryImages[selectedImageIndex]?.image_url}
                  alt="Slider"
                  className="max-h-[90vh] max-w-[90vw] mx-auto rounded-md z-40"
                />
              )}

              {/* السهم لليمين */}
              {selectedImageIndex < galleryImages.length - 1 && (
                <span
                  className="absolute right-4 z-50 flex items-center justify-center 
                 w-12 h-12 rounded-full bg-black bg-opacity-60 
                 text-white text-3xl shadow-lg cursor-pointer 
                 hover:bg-opacity-80 transition duration-200"
                  onClick={handleNextImage}
                >
                  &#10095;
                </span>
              )}
            </div>

          </div>
        )}
      </div>
    </>
  );
}

export default SectionOne;
